import { useLayoutEffect, useState, type RefObject } from 'react'

interface AnchoredCoords {
  top: number
  left: number
  arrowLeft: number
  ready: boolean
}

export interface AnchoredPositionOptions {
  gap?: number
  /**
   * How much of the panel sits left of the trigger center.
   * 0.5 = fully centered, 0.35 = ~35% left / ~65% opens to the right.
   */
  panelAnchorRatio?: number
  /** Align panel to trigger start, center, or end. */
  align?: 'start' | 'center' | 'end'
}

const VIEWPORT_MARGIN = 16

/** Position a fixed panel under a trigger, clamped to the viewport. */
export function useAnchoredPosition(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  options: AnchoredPositionOptions = {},
): AnchoredCoords {
  const { gap = 4, panelAnchorRatio = 0.35, align = 'center' } = options
  const [coords, setCoords] = useState<AnchoredCoords>({ top: 0, left: 0, arrowLeft: 20, ready: false })

  useLayoutEffect(() => {
    if (!open) {
      setCoords((c) => ({ ...c, ready: false }))
      return
    }

    const update = () => {
      const trigger = triggerRef.current
      const panel = panelRef.current
      if (!trigger || !panel) return

      const tr = trigger.getBoundingClientRect()
      const pw = panel.offsetWidth
      const anchorX = tr.left + tr.width / 2
      let left =
        align === 'start'
          ? tr.left
          : align === 'end'
            ? tr.right - pw
            : anchorX - pw * panelAnchorRatio
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - pw - VIEWPORT_MARGIN))

      const arrowHalf = 5
      let arrowLeft = anchorX - left - arrowHalf
      arrowLeft = Math.max(16, Math.min(arrowLeft, pw - 16))

      setCoords({
        top: tr.bottom + gap,
        left,
        arrowLeft,
        ready: true,
      })
    }

    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, triggerRef, panelRef, gap, panelAnchorRatio, align])

  return coords
}
